import { useEffect, useRef } from "react";
import { Modal } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import { modalWrapStyle } from "../../../../../utils/modalStyles";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
  posModalStyles as modalStyles,
} from "../../modalPrimitives";

interface ReservationConfirmModalProps {
  open: boolean;
  /** „Маса 3 е резервирана за 19:30 — Иванов, 4 гости. Продължаваш ли?" или
   *  „Маса 3 е резервирана. Да отворим ли маса 4?" (`useReservationConfirm`). */
  text: string;
  /** Етикетът на основния бутон: „Продължи" / „Отвори маса 4". `null` —
   *  само съобщение (другата маса на настанена резервация не се отваря):
   *  един бутон „Разбрах", който отказва. */
  continueLabel: string | null;
  onCancel: () => void;
  onContinue: () => void;
}

/**
 * „Масата има резервация — продължаваш ли?" (Маркиране). Само пита:
 * основният бутон пуска действието нормално, „Отказ" го спира. Резервацията
 * не се пипа. Същият шел пита и „Да отворим ли маса N?" след „Отказ" при
 * бързото отваряне (`useReservationConfirm.offerFreeTable`).
 *
 * Отваря се ВЪРХУ други модали с глобални клавиши („Нова маса" пише цифри и
 * приема Enter/Esc от `window`). Затова слушаме в capture фазата на `window` и
 * спираме разпространението — Esc/Enter/цифрите стигат само до този модал.
 */
export const ReservationConfirmModal = ({
  open,
  text,
  continueLabel,
  onCancel,
  onContinue,
}: ReservationConfirmModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const handlersRef = useRef({ onCancel, onContinue });
  handlersRef.current = { onCancel, onContinue };
  const blocked = continueLabel === null;
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") return;
      event.stopImmediatePropagation();
      if (event.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
        event.preventDefault();
        handlersRef.current.onCancel();
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (blockedRef.current) handlersRef.current.onCancel();
        else handlersRef.current.onContinue();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      closable={false}
      keyboard={false}
      destroyOnHidden
      zIndex={1100}
      width={isMobile ? undefined : isTablet ? 420 : 380}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<CalendarOutlined />}
          title={t(blocked ? "ordering.reservationNotice.blockedTitle" : "ordering.reservationNotice.title")}
          onClose={onCancel}
          isNarrow={isNarrow}
        />
        <div className={isNarrow ? "p-4" : "px-6 py-5"}>
          <p className="m-0 text-[14.5px] font-semibold leading-normal text-pos-ink2">
            {text}
          </p>
        </div>
        <ModalFooter isNarrow={isNarrow}>
          {blocked ? (
            <PrimaryButton
              label={t("ordering.reservationNotice.understood")}
              onClick={onCancel}
              shortcut={isDesktop ? "⏎" : undefined}
            />
          ) : (
            // Масив, не фрагмент: ModalFooter брои децата си през Children.toArray.
            [
              <GhostButton
                key="cancel"
                label={t("common.cancel")}
                onClick={onCancel}
                shortcut={isDesktop ? "Esc" : undefined}
              />,
              <PrimaryButton
                key="continue"
                label={continueLabel}
                onClick={onContinue}
                shortcut={isDesktop ? "⏎" : undefined}
              />,
            ]
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
};
